import { Chart } from '@evogard/ui/chart';
import { businessDate, format, fromColumn, type Money } from '@evogard/domain';
import type { Period, StatementDay } from '@evogard/contract';
import { periodLabel } from './common.js';

/**
 * O extrato como FIGURA — a entrada de cada dia em barra e o acumulado do mês
 * em linha, no mesmo eixo, porque os dois são reais.
 *
 * ## O dado é `days`, e não as entradas da página
 *
 * `days` vem medido sobre o período inteiro, com o mesmo filtro da listagem
 * (ver `statementDaySchema`). Desenhar as entradas da página daria um mês que
 * termina onde a página termina, e o acumulado pararia num número que não é o
 * total de lugar nenhum. Por isso a figura não pagina, e o último ponto da linha
 * é o total do cartão do topo.
 *
 * ## O dia sem entrada é ZERO, e isso é medida, não suposição
 *
 * O servidor devolve um grupo por dia que TEM entrada no período. O dia que não
 * aparece não teve boleto pago que gerasse entrada — é zero de verdade, e é o
 * que a barra diz. Sem preencher, a figura pularia do dia 3 para o dia 17 com a
 * mesma distância que do 3 para o 4, e o ritmo do mês ficaria ilegível.
 *
 * O preenchimento vai até o último dia do mês, ou até HOJE no mês corrente: o
 * dia que ainda não chegou não é zero, é o que não aconteceu, e uma barra vazia
 * ali diria que ele passou sem nada.
 *
 * ## O fecho do mês é a última categoria
 *
 * O grupo sem data (`date === null`) é a premiação por faixa de placas, que só
 * entra no fechamento. Ele não é movimento de um dia, e por isso não se mistura
 * a nenhum: vem depois do último dia, com o nome dele, e fecha a linha no total.
 */
export function StatementChart({
  days,
  month,
  filtered,
}: {
  days: readonly StatementDay[];
  month: Period;
  filtered: boolean;
}) {
  let points: DailyPoint[] = [];
  let error: Error | null = null;
  try {
    points = dailyPoints(days, month);
  } catch (e) {
    error = e instanceof Error ? e : new Error(String(e));
  }

  return (
    <Chart<DailyPoint>
      kind="barra-linha"
      title="Entradas por dia"
      descricao={
        filtered
          ? `Só as entradas que casam com a busca, em ${periodLabel(month)}.`
          : `A entrada de cada dia de ${periodLabel(month)} e quanto o mês já somou.`
      }
      label={`Entradas por dia em ${periodLabel(month)}`}
      data={points}
      error={error}
      empty={`Nenhuma entrada em ${periodLabel(month)}.`}
      category={(p) => p.axisLabel}
      series={[
        { key: 'day', label: 'Entrada do dia', mark: 'barra', value: (p) => p.amount },
        { key: 'total', label: 'Acumulado no mês', mark: 'linha', value: (p) => p.accumulated },
      ]}
      format={(cents) => format(cents as Money)}
      axisFormat={compactMoney}
      height={300}
      tooltip={({ item }) => <DayReport point={item} />}
    />
  );
}

/** Um ponto da figura: um dia do mês, ou o fecho dele. */
export interface DailyPoint {
  /** `AAAA-MM-DD`, ou nulo no fecho do mês. */
  date: string | null;
  axisLabel: string;
  /** Centavos que entraram no dia. */
  amount: number;
  /** Centavos somados do dia 1 até este, inclusive. */
  accumulated: number;
  rows: number;
}

/**
 * Os dias do mês em ordem, com zero onde não houve entrada, e o fecho no fim.
 *
 * Um dia cujo total não é legível (`fromColumn` recusa) derruba a figura
 * inteira, em vez de virar zero: o acumulado depois dele estaria errado em
 * todos os pontos, e a figura não tem onde dizer "ilegível" como a lista diz.
 */
export function dailyPoints(
  days: readonly StatementDay[],
  month: Period,
  today: string = businessDate(new Date()),
): DailyPoint[] {
  if (days.length === 0) return [];

  const byDate = new Map<string, StatementDay>();
  let closing: StatementDay | undefined;
  for (const day of days) {
    if (day.date === null) closing = day;
    else byDate.set(day.date, day);
  }

  const [year, monthNumber] = month.split('-').map(Number) as [number, number];
  const monthEnd = new Date(Date.UTC(year, monthNumber, 0)).getUTCDate();
  const lastDay = today.startsWith(month) ? Number(today.slice(8, 10)) : monthEnd;

  const points: DailyPoint[] = [];
  let accumulated = 0;
  for (let d = 1; d <= lastDay; d++) {
    const dd = String(d).padStart(2, '0');
    const date = `${month}-${dd}`;
    const day = byDate.get(date);
    const amount = day ? centsOf(day) : 0;
    accumulated += amount;
    points.push({
      date,
      axisLabel: `${dd}/${month.slice(5, 7)}`,
      amount,
      accumulated,
      rows: day?.rows ?? 0,
    });
  }

  /*
   * Um dia com entrada DEPOIS de hoje — o relógio da máquina atrasado, ou um
   * boleto com data de pagamento adiantada no SGA. Ele não some: some junto
   * com ele o valor, e o último ponto deixaria de ser o total.
   */
  for (const [date, day] of [...byDate].sort(([a], [b]) => a.localeCompare(b))) {
    if (Number(date.slice(8, 10)) <= lastDay) continue;
    const amount = centsOf(day);
    accumulated += amount;
    points.push({
      date,
      axisLabel: `${date.slice(8, 10)}/${date.slice(5, 7)}`,
      amount,
      accumulated,
      rows: day.rows,
    });
  }

  if (closing) {
    const amount = centsOf(closing);
    accumulated += amount;
    points.push({
      date: null,
      axisLabel: 'Fecho',
      amount,
      accumulated,
      rows: closing.rows,
    });
  }

  return points;
}

function centsOf(day: StatementDay): number {
  const cents = fromColumn(day.total);
  if (cents === null) {
    throw new Error(`O total do dia ${day.date ?? 'do fecho'} não é legível: ${day.total}`);
  }
  return cents;
}

/**
 * O relatório da dica — o dia, o que entrou nele, em quantas entradas, e o
 * acumulado até ali.
 */
function DayReport({ point }: { point: DailyPoint }) {
  return (
    <>
      <p className="font-semibold text-texto">
        {point.date === null ? 'Fecho do mês' : dayLabel(point.date)}
      </p>
      {point.date === null && (
        <p className="mt-0.5 text-texto-suave">
          As premiações por faixa de placas, apuradas no fechamento.
        </p>
      )}
      <dl className="mt-1.5 grid grid-cols-[auto_auto] gap-x-3 gap-y-1">
        <dt className="text-texto-suave">
          {point.date === null ? 'Entrou no fecho' : 'Entrou no dia'}
        </dt>
        <dd
          className={
            point.amount > 0
              ? 'text-right font-semibold text-sucesso tabular-nums'
              : 'text-right font-medium text-texto tabular-nums'
          }
        >
          {point.amount > 0 && '+ '}
          {format(point.amount as Money)}
        </dd>
        <dt className="text-texto-suave">Entradas</dt>
        <dd className="text-right font-medium text-texto tabular-nums">
          {point.rows === 0 ? 'nenhuma' : point.rows.toLocaleString('pt-BR')}
        </dd>
        <dt className="text-texto-suave">Acumulado no mês</dt>
        <dd className="text-right font-medium text-texto tabular-nums">
          {format(point.accumulated as Money)}
        </dd>
      </dl>
    </>
  );
}

/** O eixo em reais redondos — `R$ 1,2 mil`. O número exato é o da dica. */
export function compactMoney(cents: number): string {
  return new Intl.NumberFormat('pt-BR', {
    style: 'currency',
    currency: 'BRL',
    notation: 'compact',
    maximumFractionDigits: 1,
  }).format(cents / 100);
}

/** `2026-09-22` → `22 de setembro`, como o cabeçalho do dia na lista. */
function dayLabel(date: string): string {
  return new Intl.DateTimeFormat('pt-BR', {
    day: 'numeric',
    month: 'long',
    timeZone: 'UTC',
  }).format(new Date(`${date}T12:00:00Z`));
}
